import React, { useState, useEffect } from 'react';
import {
  Rocket,
  ArrowRight,
  CheckCircle,
  CreditCard,
  ShieldCheck,
  Check,
  Lock,
  Sparkles,
  ArrowLeft,
  Building2,
  Loader2
} from 'lucide-react';
import '../styles/onboarding.css';
import { API_BASE } from '../services/api';

function Onboarding({ onComplete }) {
  const [step, setStep] = useState(1);

  // Step 1 State
  const [selectedPlan, setSelectedPlan] = useState('hobby');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const [packages, setPackages] = useState([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  // Safety guard: If user already has a pre-assigned plan, bypass onboarding
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        const hasExistingPlan = Boolean(
          u.packageTier === 'custom' ||
          u.customLimits ||
          u.createdBy ||
          (u.packageTier && u.packageTier !== 'hobby' && u.subscriptionStatus === 'active')
        );
        if (hasExistingPlan) {
          onComplete && onComplete();
        }
      }
    } catch (_) {}
  }, [onComplete]);

  // Load packages and initial plan
  useEffect(() => {
    const plan = localStorage.getItem('selectedPlan');
    if (plan) {
      setSelectedPlan(plan);
    }

    // Check if returning from Stripe Hosted Checkout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true' || urlParams.get('session_id')) {
      setStep(3);
    }

    // Fetch real packages from backend
    import('../services/api').then(({ default: api }) => {
      api.getPublicPackages()
        .then((res) => {
          setPackages(res.packages || []);
          setPlanLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load packages:', err);
          setPlanLoading(false);
        });
    });
  }, []);

  // Determine current selected package details
  const activePackage =
    (packages && packages.length > 0)
      ? packages.find((p) => p.id === selectedPlan || p.tierId === selectedPlan)
      : null;

  const pkgPrice = activePackage ? parseFloat(activePackage.price) : (selectedPlan === 'pro' ? 20 : 0);
  const isCustomPrice =
    activePackage &&
    (isNaN(pkgPrice) || (typeof activePackage.price === 'string' && activePackage.price.toLowerCase() === 'custom'));
  const isPaidPlan = activePackage ? (!isNaN(pkgPrice) && pkgPrice > 0) : (selectedPlan !== 'hobby');

  // Steps dynamically adjust based on whether the plan is paid
  const getStepsList = () => {
    if (isPaidPlan) {
      return [
        { id: 1, title: 'Subscription', desc: 'Choose your package' },
        { id: 2, title: 'Payment', desc: 'Official Stripe Checkout' },
        { id: 3, title: 'Complete', desc: 'Ready to deploy' }
      ];
    }
    return [
      { id: 1, title: 'Subscription', desc: 'Choose your package' },
      { id: 2, title: 'Complete', desc: 'Ready to deploy' }
    ];
  };

  const stepsList = getStepsList();
  const maxStep = stepsList.length;

  // Plan Details Helper
  const getPlanDetails = () => {
    if (activePackage) {
      const basePrice = parseFloat(activePackage.price) || 0;
      return {
        name: activePackage.tier,
        priceMonthly: `$${basePrice}`,
        priceAnnual: `$${basePrice * 10}`,
        amountNumber: billingCycle === 'annual' ? basePrice * 10 : basePrice,
        features: activePackage.features || []
      };
    }
    if (selectedPlan === 'pro') {
      return {
        name: 'Pro Agency',
        priceMonthly: '$20',
        priceAnnual: '$200',
        amountNumber: billingCycle === 'annual' ? 200 : 20,
        features: ['Unlimited Deployments', 'CI/CD Integration', 'Automated Daily Backups', 'Up to 5 team members']
      };
    }
    return {
      name: 'Hobby',
      priceMonthly: '$0',
      priceAnnual: '$0',
      amountNumber: 0,
      features: ['Up to 1 active deployment', 'ZIP and Docker Hub deploys', 'Community support']
    };
  };

  const planInfo = getPlanDetails();

  // Redirect to official Stripe Hosted Checkout
  const handleStripeCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: selectedPlan,
          billingCycle
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to initiate Stripe checkout session.');
      }

      const data = await response.json();
      if (data.isCustom) {
        window.location.href = 'mailto:sales@devorants.com?subject=Enterprise%20Plan%20Inquiry';
        setCheckoutError(data.message || 'Please contact sales.');
        setCheckoutLoading(false);
        return;
      }

      if (data.url) {
        localStorage.setItem('onboarding_pending_payment', 'true');
        // Redirect to Stripe's official hosted checkout page
        window.location.href = data.url;
      }
    } catch (err) {
      setCheckoutError(err.message || 'Payment processing failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Step Forward Handler
  const nextStep = async () => {
    if (step === 1) {
      if (isCustomPrice) {
        window.location.href = 'mailto:sales@devorants.com?subject=Enterprise%20Plan%20Inquiry';
        return;
      }
      if (isPaidPlan) {
        setStep(2);
        return;
      }

      // Free plan: Save onboarding details and advance to complete
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE}/onboarding-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({})
        });
      } catch (err) {
        console.error('Failed to save onboarding details:', err);
      }
      setStep(2);
      return;
    }

    if (step === 2 && isPaidPlan) {
      await handleStripeCheckout();
      return;
    }

    // Completion Step
    const completeStep = isPaidPlan ? 3 : 2;
    if (step >= completeStep) {
      onComplete && onComplete();
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Filter packages: Core tiers vs Enterprise custom
  const allPackagesList =
    packages.length > 0
      ? packages
      : [
          { id: 'hobby', tier: 'Hobby', price: 0, features: ['Up to 1 active deployment', 'ZIP and Docker Hub deploys', 'Community support'] },
          { id: 'pro', tier: 'Pro', price: 5, features: ['Up to 5 active deployments', 'ZIP and Docker Hub deploys', 'Community support'] },
          { id: 'pro_agency', tier: 'Pro Agency', price: 20, features: ['Unlimited deployments', 'GitHub/GitLab CI/CD integration', 'Automated daily backups', 'Up to 5 team members'] },
          { id: 'enterprise', tier: 'Enterprise', price: 'Custom', features: ['Super-admin white-labeling', 'Unlimited dedicated resources', 'Custom SLA & 24/7 support'] }
        ];

  const corePackages = allPackagesList.filter((p) => {
    const val = parseFloat(p.price);
    const isCustom = isNaN(val) || (typeof p.price === 'string' && p.price.toLowerCase() === 'custom');
    return !isCustom;
  });

  const enterprisePackage = allPackagesList.find((p) => {
    const val = parseFloat(p.price);
    return isNaN(val) || (typeof p.price === 'string' && p.price.toLowerCase() === 'custom');
  });

  // Render Step 1, Step 2 (Stripe Mock Checkout), or Step 3 (Complete)
  const renderStepContent = () => {
    // ── STEP 1: CHOOSE PLAN ──
    if (step === 1) {
      return (
        <div className="onb-form-section">
          <div className="onb-header-group">
            <h2 className="onb-title">Choose your Plan</h2>
            <p className="onb-subtitle">Select the package that best fits your development and deployment needs.</p>
          </div>

          {/* Monthly / Annual Toggle Switch */}
          <div className="onb-toggle-wrap">
            <div className="onb-toggle-group">
              <button
                type="button"
                className={`onb-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`onb-toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                onClick={() => setBillingCycle('annual')}
              >
                Annual <span className="onb-save-badge">Save 20%</span>
              </button>
            </div>
          </div>

          {planLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <Loader2 size={24} className="lp-spin" style={{ margin: '0 auto 0.5rem' }} />
              <div>Loading pricing packages...</div>
            </div>
          ) : (
            <>
              {/* 3 Core Pricing Cards Grid */}
              <div className="onb-plans-grid">
                {corePackages.map((pkg) => {
                  const basePrice = parseFloat(pkg.price) || 0;
                  const isFree = basePrice === 0;
                  const isSelected = selectedPlan === pkg.id || selectedPlan === pkg.tierId;
                  const isPopular = pkg.tier.toLowerCase().includes('agency') || pkg.tier.toLowerCase() === 'pro';

                  let priceDisplay = isFree
                    ? 'Free'
                    : billingCycle === 'annual'
                    ? `$${basePrice * 10}`
                    : `$${basePrice}`;

                  let billingSubtitle = isFree
                    ? 'Free forever'
                    : billingCycle === 'annual'
                    ? 'Billed annually'
                    : 'Billed monthly';

                  return (
                    <div
                      key={pkg.id || pkg.tierId}
                      className={`onb-plan-card ${isSelected ? 'active' : ''} ${isPopular ? 'popular' : ''}`}
                      onClick={() => setSelectedPlan(pkg.id || pkg.tierId)}
                    >
                      {isPopular && (
                        <div className="onb-popular-badge">
                          <Sparkles size={12} /> MOST POPULAR
                        </div>
                      )}

                      <div className="onb-plan-header">
                        <div className="onb-plan-title-row">
                          <h3 className="onb-plan-name">{pkg.tier}</h3>
                          <div className={`onb-radio-indicator ${isSelected ? 'checked' : ''}`}>
                            {isSelected && <div className="onb-radio-dot" />}
                          </div>
                        </div>
                        <span className="onb-plan-subtitle">{billingSubtitle}</span>
                        <div className="onb-price-box">
                          <span className="onb-price-amount">{priceDisplay}</span>
                          {!isFree && (
                            <span className="onb-price-period">
                              {billingCycle === 'annual' ? '/year' : '/month'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="onb-features-divider" />

                      <ul className="onb-features-list">
                        {(pkg.features || []).map((feature, i) => (
                          <li key={i} className="onb-feature-item">
                            <CheckCircle size={16} className="onb-feature-check" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Enterprise Solutions Full-Width Banner */}
              {enterprisePackage && (
                <div
                  className={`onb-enterprise-banner ${
                    selectedPlan === enterprisePackage.id || selectedPlan === enterprisePackage.tierId ? 'active' : ''
                  }`}
                  onClick={() => setSelectedPlan(enterprisePackage.id || enterprisePackage.tierId)}
                >
                  <div className="onb-ent-left">
                    <div className="onb-ent-icon">
                      <Building2 size={24} />
                    </div>
                    <div>
                      <div className="onb-ent-badge">Custom Architecture</div>
                      <h3 className="onb-ent-title">{enterprisePackage.tier}</h3>
                      <p className="onb-ent-desc">
                        Tailored infrastructure, white-labeling, dedicated servers, and custom SLA for scaling teams.
                      </p>
                    </div>
                  </div>

                  <div className="onb-ent-right">
                    <div className="onb-ent-price-box">
                      <span className="onb-ent-price">Custom</span>
                      <span className="onb-ent-sub">Contact Sales</span>
                    </div>
                    <div className={`onb-radio-indicator ${selectedPlan === enterprisePackage.id ? 'checked' : ''}`}>
                      {selectedPlan === enterprisePackage.id && <div className="onb-radio-dot" />}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!isPaidPlan && !planLoading && !isCustomPrice && (
            <div className="onb-free-notice">
              <CheckCircle size={16} color="#10b981" />
              <span>No payment required for Hobby. You can upgrade anytime from Billing settings.</span>
            </div>
          )}
        </div>
      );
    }

    // ── STEP 2: STRIPE OFFICIAL CHECKOUT CONFIRMATION ──
    if (isPaidPlan && step === 2) {
      return (
        <div className="onb-form-section">
          <div className="onb-header-group">
            <h2 className="onb-title">Checkout with Stripe</h2>
            <p className="onb-subtitle">
              You will be securely redirected to Stripe's official checkout page to complete your {planInfo.name} subscription.
            </p>
          </div>

          <div className="onb-checkout-summary-card">
            <div className="onb-summary-row-main">
              <div>
                <span className="onb-summary-badge">SELECTED PLAN</span>
                <h3 className="onb-summary-plan-name">{planInfo.name} Plan</h3>
                <span className="onb-summary-cycle-label">
                  {billingCycle === 'annual' ? 'Billed annually' : 'Billed monthly'}
                </span>
              </div>
              <div className="onb-summary-price-box">
                <span className="onb-summary-price-val">
                  {billingCycle === 'annual' ? planInfo.priceAnnual : planInfo.priceMonthly}
                </span>
                <span className="onb-summary-period-sub">
                  {billingCycle === 'annual' ? '/year' : '/month'}
                </span>
              </div>
            </div>

            <div className="onb-summary-divider" />

            <div className="onb-summary-features">
              <span className="onb-summary-features-title">Included in your plan:</span>
              <ul className="onb-summary-features-list">
                {planInfo.features.map((feat, i) => (
                  <li key={i}>
                    <Check size={14} color="#10b981" /> <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="onb-summary-divider" />

            <div className="onb-summary-totals">
              <div className="onb-total-row">
                <span>Subtotal</span>
                <span>{billingCycle === 'annual' ? planInfo.priceAnnual : planInfo.priceMonthly}</span>
              </div>
              <div className="onb-total-row">
                <span>Tax</span>
                <span>$0.00</span>
              </div>
              <div className="onb-total-row final">
                <span>Total Due Today</span>
                <span>{billingCycle === 'annual' ? planInfo.priceAnnual : planInfo.priceMonthly}</span>
              </div>
            </div>
          </div>

          {checkoutError && (
            <div className="onb-stripe-error">
              <span>{checkoutError}</span>
            </div>
          )}

          <div className="onb-stripe-trust-badge">
            <ShieldCheck size={18} color="#10b981" />
            <span>
              Payments are 256-bit encrypted and securely processed via Stripe. Your payment credentials never touch our servers.
            </span>
          </div>
        </div>
      );
    }

    // ── STEP 3: ALL SET / COMPLETED ──
    const completeStep = isPaidPlan ? 3 : 2;
    if (step === completeStep) {
      return (
        <div className="onb-complete-container">
          <div className="onb-complete-icon-wrap">
            <CheckCircle size={44} color="#10b981" />
          </div>
          <h2 className="onb-complete-title">You're all set!</h2>
          <p className="onb-complete-subtitle">
            {isPaidPlan
              ? `Your ${planInfo.name} subscription is active and verified! Your workspace is ready.`
              : `You are on the Hobby plan (Free forever). You can upgrade anytime from Billing settings.`}
          </p>

          <div className="onb-complete-card">
            <div className="onb-complete-card-header">
              <span className="onb-complete-badge">ACTIVE SUBSCRIPTION</span>
              <h3 className="onb-complete-plan">{planInfo.name}</h3>
            </div>
            <ul className="onb-complete-features">
              {planInfo.features.map((feat, i) => (
                <li key={i}>
                  <Check size={14} color="#10b981" /> <span>{feat}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    return null;
  };

  // Footer button text
  const getFooterButtonText = () => {
    const completeStep = isPaidPlan ? 3 : 2;
    if (step === completeStep) {
      return (
        <>
          <span>Go to Dashboard</span> <Rocket size={17} />
        </>
      );
    }
    if (step === 1) {
      if (isCustomPrice) {
        return (
          <>
            <span>Contact Enterprise Sales</span> <ArrowRight size={17} />
          </>
        );
      }
      if (isPaidPlan) {
        return (
          <>
            <span>Proceed to Checkout</span> <ArrowRight size={17} />
          </>
        );
      }
      return (
        <>
          <span>Continue Free</span> <ArrowRight size={17} />
        </>
      );
    }
    if (isPaidPlan && step === 2) {
      return (
        <>
          <span>{checkoutLoading ? 'Redirecting to Stripe...' : 'Proceed to Stripe Checkout'}</span>
          <CreditCard size={17} />
        </>
      );
    }
    return (
      <>
        <span>Continue</span> <ArrowRight size={17} />
      </>
    );
  };

  return (
    <div className="onb-root">
      {/* Sidebar Navigation */}
      <aside className="onb-sidebar">
        <div className="onb-sidebar-logo">
          <div className="onb-logo-icon">
            <Rocket size={20} color="#2563eb" />
          </div>
          <span>Devorants</span>
        </div>

        <ul className="onb-nav-list">
          {stepsList.map((s) => {
            const isCompleted = step > s.id;
            const isActive = step === s.id;
            return (
              <li
                key={s.id}
                className={`onb-nav-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="onb-nav-icon">
                  {isCompleted ? <Check size={13} strokeWidth={3} /> : s.id}
                </div>
                <div className="onb-nav-text">
                  <span className="onb-nav-title">{s.title}</span>
                  <span className="onb-nav-desc">{s.desc}</span>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="onb-sidebar-footer">
          <ShieldCheck size={16} color="#10b981" />
          <span>Enterprise Grade Platform</span>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="onb-main">
        <div
          className="onb-content-wrapper"
          key={step}
          style={{ maxWidth: step === 1 ? '1040px' : step === 2 ? '700px' : '620px' }}
        >
          {renderStepContent()}

          {/* Footer Actions */}
          <div className="onb-footer">
            {step > 1 ? (
              <button
                type="button"
                className="onb-skip-btn"
                onClick={prevStep}
                disabled={checkoutLoading || planLoading}
              >
                <ArrowLeft size={16} /> <span>Go Back</span>
              </button>
            ) : (
              <div />
            )}

            <button
              type="button"
              className="onb-submit-btn"
              onClick={nextStep}
              disabled={checkoutLoading || planLoading}
            >
              {getFooterButtonText()}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Onboarding;
